import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';

import { onEvent } from 'helpers/EventHelpers';
import { titaniteView } from 'apis/amethyst';
import { offset } from 'helpers/ClientUtils';
import { pageTypeChange } from 'actions/common';
import { firePixelServer } from 'actions/pixelServer';
import { fetchCollectionList, getLists } from 'actions/hearts';
import { isCollectionAndListOutOfSync, makeCollectionInformationWithItems } from 'helpers/HeartUtils';
import CollectionListCard from 'components/account/Collections/CollectionListCard';
import DeleteCollectionModal from 'components/account/Collections/DeleteCollectionModal';
import NewCollectionModal from 'components/account/NewCollectionModal';
import ShareCollectionModal from 'components/account/Collections/ShareCollectionModal';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import AccountHeader from 'containers/account/AccountHeader';
import css from 'styles/containers/account/collections.scss';

// used on the old favorites page
const LAZYLOAD_HEARTS_OFFSET = 500;

export class Collections extends Component {
  constructor(props) {
    super(props);
    this.loadMore = createRef();
    this.onScrollPage = throttle(this.onScrollPage.bind(this), 100);
  }

  state = {
    showNewCollectionModal: false,
    showShareCollectionModal: false,
    showDeleteCollectionModal: false,
    currentlyFocusedListId: null
  }

  componentDidMount() {
    titaniteView();
    this.fetchData();
    this.setupScrollHandler();
  }

  setupScrollHandler = () => {
    this.scrollHandler = onEvent(window, 'scroll', this.onScrollPage, null, this);
  }

  onScrollPage = () => {
    const { scrollY, pageYOffset, innerHeight } = window;
    const loadMore = this.loadMore.current;

    if (!loadMore) {
      this.scrollHandler.unbind();
      this.scrollHandler = null; // remove the handler event so the other methods know to resetup
      return;
    }

    const { getLists, hearts: { collectionNextPageToken } } = this.props;
    const offsetScroll = (scrollY || pageYOffset) + innerHeight + LAZYLOAD_HEARTS_OFFSET;
    if (offsetScroll > offset(loadMore).top) {
      // unbind scroll while we fetch lists then hearts);
      this.scrollHandler.unbind();
      getLists({ nextPageToken: collectionNextPageToken, concat: true }).then(() => {
        // rebind scroll handler and get lists data after finishing the list call
        this.fetchListData();
        if (loadMore) {
          this.setupScrollHandler();
        }
      });
    }
  }

  fetchListData = () => {
    const { hearts: { lists }, fetchCollectionList } = this.props;
    lists?.forEach(({ listId }) => {
      fetchCollectionList({ listId });
    });
  }

  fetchData = () => {
    const { hearts: { lists, collections }, getLists } = this.props;

    if (isCollectionAndListOutOfSync(lists, collections)) {
      getLists().then(this.fetchListData);
    }
  }

  onStartNewCollection = e => {
    e.preventDefault();
    this.setState({ showNewCollectionModal: true });
  }

  onSaveNewCollection = () => {
    const { getLists } = this.props;
    this.setState({ showNewCollectionModal: false });
    getLists().then(this.fetchListData);

    // upon deletion or save there is a refetch and if you hit the bottom
    // the handler doesnt get rebinded
    if (!this.scrollHandler) {
      this.setupScrollHandler();
    }
  }

  onDeleteCollection = () => {
    const { getLists } = this.props;
    this.setState({ showDeleteCollectionModal: false, currentlyFocusedListId: null });
    getLists().then(this.fetchListData);

    // upon deletion or save there is a refetch and if you hit the bottom
    // the handler doesnt get rebinded
    if (!this.scrollHandler) {
      this.setupScrollHandler();
    }
  }

  onShareCollection = () => {
    this.setState({ showShareCollectionModal: false, currentlyFocusedListId: null });
  }

  onOpenShareModal = e => {
    e.preventDefault();
    const listId = e.target.dataset.listid;
    this.setState({ showShareCollectionModal: true, currentlyFocusedListId: listId });
  }

  onOpenDeleteModal = e => {
    e.preventDefault();
    const listId = e.target.dataset.listid;
    this.setState({ showDeleteCollectionModal: true, currentlyFocusedListId: listId });
  }

  render() {
    const { hearts: { lists, collections, collectionNextPageToken } } = this.props;
    const { showNewCollectionModal, showDeleteCollectionModal, showShareCollectionModal, currentlyFocusedListId } = this.state;
    const collectionWithItems = makeCollectionInformationWithItems(lists, collections);

    return (
      <SiteAwareMetadata>
        <div className={css.collections}>
          <NewCollectionModal showNewCollectionModal={showNewCollectionModal} onDoneModal={this.onSaveNewCollection} />
          <DeleteCollectionModal showDeleteCollectionModal={showDeleteCollectionModal} listId={currentlyFocusedListId} onDoneModal={this.onDeleteCollection} />
          <ShareCollectionModal showShareCollectionModal={showShareCollectionModal} listId={currentlyFocusedListId} onDoneModal={this.onShareCollection}/>
          <div className={css.headingContainer}>
            <div className={css.accountHeader}>
              <AccountHeader title="Your Collections" showFirstName={false} showCustomerEmail={false}/>
            </div>
            <button className={css.newCollection} type="button" onClick={this.onStartNewCollection}>Start New Collection</button>
          </div>
          <section className={css.collectionListContainer} key={`${collectionWithItems.length}-collectionContainer`}>
            { collectionWithItems.length ? collectionWithItems.map(collection => (
              <CollectionListCard
                key={collection.listId}
                listId={collection.listId}
                hearts={collection.hearts}
                name={collection.name}
                itemCount={collection.itemCount}
                onOpenShareModal={this.onOpenShareModal}
                onOpenDeleteModal={this.onOpenDeleteModal}
              />
            )) : <PageLoader centered={true}/> }
          </section>
          { !!collectionNextPageToken &&
            <button type="button" className={css.loadMore} ref={this.loadMore}>More</button>
          }
        </div>
      </SiteAwareMetadata>
    );
  }
}

export const mapStateToProps = state => ({
  hearts: state.hearts
});

export default connect(mapStateToProps,{
  fetchCollectionList,
  getLists,
  firePixelServer,
  pageTypeChange
})(Collections);
